import {LevelZoneService} from '@app/layout/sites/manage/manage-sites/upsert-site/forms/level-zone-form/level-zone.service';
import {ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Level, Zone} from '@app/graphql-typings/graphql';
import {Observable, of, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {MessageTranslateService} from '@shared/services';

@Component({
  selector: 'app-level-zone-select',
  templateUrl: './level-zone-select.component.html',
  styleUrls: ['./level-zone-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LevelZoneSelectComponent), multi: true},
  ],
  standalone: false
})
export class LevelZoneSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() loading = false;
  @Input() appendTo: string;
  @Input() siteId: number;
  @Input() zoneOptional = false;
  @Input() levels: Level[];
  @Input() levels$: Observable<Level[]>;
  @Input() multiSelect = false;
  @Input() placeHolders = {level: 'Level', zone: 'Zone'};

  @Output() changeEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() touched: EventEmitter<any> = new EventEmitter<any>();
  @Output() levelsLoaded: EventEmitter<Level[]> = new EventEmitter<Level[]>();


  formControl: UntypedFormControl;
  compareFunctions: Array<(a: any, b: any) => boolean>;
  dependentDisabledMessages: string[];

  private readonly logger: AceLogger;
  private ngUnsubscribe = new Subject<void>();
  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private levelZoneService: LevelZoneService,
              private translateService: MessageTranslateService,
              logService: AceLoggerService) {
    this.logger = logService.create(LevelZoneSelectComponent.name);
    this.formControl = new UntypedFormControl();
    this.dependentDisabledMessages = [this.translateService.instant('Please select a Level first')];
    this.compareFunctions = [this.buildCompareFunction('level_id'), this.buildCompareFunction('zone_id')];
  }

  ngOnInit() {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: any[]) => {
      this.handleValueChanges(value);
    });

    // Levels may be passed to the component, but if not, the component will fetch levels itself
    if (!this.levels) {
      this.loading = true;
      if (!this.levels$) {
        if (this.siteId != null) {
          this.levels$ = this.levelZoneService.getLevelsBySiteId(this.siteId);
        } else {
          this.logger.warn('Levels and siteId are both not initialised. The component will be empty');
          this.levels$ = of([]);
        }
      }
      this.levels$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((levels: Level[]) => {
        this.levels = levels;
      }).add(() => this.loading = false);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(levelOrZones: Level | Zone | Zone[]): void {
    /* {emitEvent: false} is required so that when a parent form performs a form.reset() and writeValue receives a null value,
    * we don't then emit this null value back to the parent form via the onChange handler. Doing so, would mark parent form as touched
    * and therefore display required validation errors */
    this.formControl.setValue(this.levelOrZonesToCascadingSelect(levelOrZones), {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formControl.disable({emitEvent: false});
    } else {
      this.formControl.enable({emitEvent: false});
    }
  }

  onLevelZoneTouched() {
    this.onTouched();
    this.touched.emit();
  }

  onItemsInitialised() {
    if (this.levels) {
      this.levelsLoaded.next(this.levels);
    }
  }

  private buildCompareFunction(propertyName: string): (a: any, b: any) => boolean {
    return (a: any, b: any) => {
      if (a != null) {
        return b != null ? a[propertyName] === b[propertyName] : false;
      } else {
        return b == null;
      }
    };
  }

  private handleValueChanges(currentValue: any[]) {
    this.onChange(this.cascadingSelectToLevelOrZones(currentValue));
    this.changeEvent.emit(this.cascadingSelectToLevelOrZones(currentValue));
  }

  private levelOrZonesToCascadingSelect(levelOrZones: Level | Zone | Zone[]): any[] | null {
    if (levelOrZones) {
      if (this.multiSelect) {

        if (!this.zoneOptional) {
          // Zone[]
          if (!Array.isArray(levelOrZones)) {
            throw new Error('Invalid value. Expected an array as multiSelect is true and zoneOptional is false');
          }
          return this.processZones(levelOrZones);

        } else {
          // Level
          if (!Array.isArray(levelOrZones)) {
            if (!this.levelZoneService.isLevel(levelOrZones)) {
              throw new Error('Invalid value. Expected a Level because value is not an array and zoneOptional is true');
            }
            return [levelOrZones, null];
          } else {
            // Zone[]
            return this.processZones(levelOrZones);
          }
        }

        // Zone
      } else if (this.levelZoneService.isZone(levelOrZones)) {
        const level = levelOrZones.level;
        if (!level || !this.levelZoneService.isLevel(level)) {
          throw new Error('Invalid value. Zone has no Level');
        }
        return [level, levelOrZones];

        // Level
      } else if (this.levelZoneService.isLevel(levelOrZones)) {
        if (!this.zoneOptional) {
          throw new Error('Invalid value. Expected a Zone as zoneOptional is false but got a Level ');
        }
        return [levelOrZones, null];
      } else {
        throw new Error('Invalid value. Expected Level or Zone or Zone[] with each Zone containg a Level');
      }
    } else {
      return null;
    }
  }

  private cascadingSelectToLevelOrZones(currentValue: any[]): Level | Zone | Zone[] {
    let result: Level | Zone | Zone[];
    if (!currentValue) {
      result = null;
    } else if (currentValue[1] == null) {
      // Level
      result = currentValue[0];
    } else {
      // Zone | Zone[]
      if (this.multiSelect) {
        currentValue[1].forEach((zone: Zone) => zone.level = currentValue[0]);
      } else {
        currentValue[1].level = currentValue[0];
      }
      result = currentValue[1];
    }
    return result;
  }

  private processZones(zones: Zone[]): any[] {
    zones.forEach((zone: Zone) => {
      if (!this.levelZoneService.isZone(zone) || !this.levelZoneService.isLevel(zone.level)) {
        throw new Error('Invalid value. Expected Zone[] with each Zone containg a Level');
      }
      if (zone.level_id !== zones[0].level_id) {
        throw new Error('Invalid Value. Expected every zone to have the same Level Id: ' + zone.level_id + ' != ' + zones[0].level_id);
      }
    });
    return [zones[0].level, zones];
  }
}
